import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, signUp, supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { isNative, appOrigin } from '../utils/native';
import { biometricAvailable, biometricSaved, saveBiometric } from '../utils/biometric';

import { BrandLogo } from '../components/common/BrandLogo';
export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [offerBio, setOfferBio] = useState(false);   // phone has a fingerprint / face and it is not turned on yet
  const [useBio, setUseBio] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNative) return;
    (async () => { if ((await biometricAvailable()) && !(await biometricSaved())) setOfferBio(true); })();
  }, []);

  const sendReset = async () => {
    if (!email) { toast.error('Enter your email address first'); return; }
    setLoading(true);
    // redirectTo must point at the reset page — without it Supabase lands the user
    // on the dashboard and the password is never actually changed.
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${appOrigin()}/reset-password`,
    });
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Check your email for a password reset link (it may land in spam)');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignUp) {
        const { error } = await signUp(email, password);
        if (error) throw error;
        toast.success('Account created! Check your email to confirm.');
      } else {
        const { error } = await signIn(email, password);
        if (error) throw error;
        if (isNative && offerBio && useBio) {
          try { await saveBiometric(email, password); toast.success('Biometric sign-in is on'); } catch { toast('Biometric sign-in was not turned on'); }
        }
        navigate('/');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand"><BrandLogo width={240} /></div>
        <div className="login-subtitle">Your collection, intelligently managed</div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          {isNative && offerBio && !isSignUp && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: 'var(--text2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={useBio} onChange={e => setUseBio(e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--accent)' }} />
              Use my fingerprint / face to sign in next time
            </label>
          )}
          <button className="btn btn-primary" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '11px' }}>
            {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        {!isSignUp && (
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <span onClick={sendReset}
              style={{ color: 'var(--text3)', cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline' }}>
              Forgot your password?
            </span>
          </div>
        )}

        {/* In the phone apps accounts are created on the website, not inside the app. */}
        {!isNative && (
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--text2)' }}>
            {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
            <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setIsSignUp(!isSignUp)}>
              {isSignUp ? 'Sign in' : 'Sign up'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};